import { usePathname } from "expo-router";
import { useEffect } from "react";
import { Platform } from "react-native";

function faviconHrefForPath(pathname: string): string {
  const path = pathname.split("?")[0] ?? "/";
  if (
    path.startsWith("/admin") ||
    path.startsWith("/super-admin") ||
    path.startsWith("/admin-licensing") ||
    path.startsWith("/admin-rm") ||
    path.startsWith("/campus")
  ) {
    return "/favicons/admin.svg";
  }
  if (path.startsWith("/client")) return "/favicons/client.svg";
  if (path.startsWith("/student")) return "/favicons/student.svg";
  return "/favicons/public.svg";
}

const ICON_ID = "coravu-dynamic-favicon";

/**
 * Web only: swap `<link rel="icon">` on route change (marketing vs student vs client vs admin surfaces).
 *
 * Also strips Expo's build-time `<link rel="icon" href="/assets/favicon-...">` PNG so it
 * doesn't compete with our SVG. Browsers can be inconsistent about which icon link wins
 * when multiple are present; this guarantees only one ever exists.
 */
export function useDynamicFavicon(): void {
  const pathname = usePathname();

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    if (!pathname) return;

    const href = faviconHrefForPath(pathname);

    // Remove any existing favicon links Expo's bundler emitted at build time, and any
    // stray duplicates from previous renders. Then ensure exactly one canonical link.
    document
      .querySelectorAll("link[rel='icon'], link[rel='shortcut icon']")
      .forEach((node) => {
        if ((node as HTMLLinkElement).id !== ICON_ID) node.remove();
      });

    let link = document.getElementById(ICON_ID) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = ICON_ID;
      link.rel = "icon";
      link.type = "image/svg+xml";
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  }, [pathname]);
}
